<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import { faTrashAlt, faSave } from '@fortawesome/free-solid-svg-icons'
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import dayjs from 'dayjs'
    import { ProductMovementsRepository } from '../../modules/posApi/stock/product-movements/product-movements.repository'
    import { ProductMovementsService } from '../../modules/posApi/stock/product-movements/product-movements.service'
    import { ProductMovement } from '../../modules/posApi/stock/product-movements/product-movement'
    import { manualProductMovementReasonOptions } from '../../modules/posApi/stock/product-movements/product-movement-reason.enum'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'

    const dispatch = createEventDispatcher()

    const rep = new ProductMovementsRepository()
    const service = new ProductMovementsService(rep)

    export let productMovementId: string = null

    let personsR = new PersonsRepository()
    let personsS = new PersonsService(personsR)

    let show = false
    let entity: ProductMovement = new ProductMovement()
    let createdAt = dayjs(new Date()).format('YYYY-MM-DD')

    onMount(async () => {
        if (productMovementId) {
            await loadEntity(productMovementId)
        } else {
            newEntity()
        }
    })

    async function loadEntity(id: string) {
        try {
            show = true
            entity = new ProductMovement()
            entity = await service.getOne(id)
            createdAt = dayjs(entity.movedAt).format('YYYY-MM-DD')
            entity.person = await personsS.getOne(entity.personId)
            console.log('entry', entity)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        entity = new ProductMovement()
        entity.movedAt = new Date(createdAt)
        entity.type = 'out_manual'
    }

    async function saveEntity() {
        if (entity.productMovementId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function createEntity() {
        try {
            if (show) return
            show = true
            if (!createdAt) {
                evaluateError('Informe data e hora de lançamento válidos')
                return false
            }
            entity.movedAt = new Date(createdAt)
            // configure timezoneoffset
            entity.movedAt.setMinutes(entity.movedAt.getMinutes() + entity.movedAt.getTimezoneOffset())
            const dto: Partial<ProductMovement> = {
                personId: entity.person?.personId,
                serviceId: entity.service?.serviceId,
                batchNumber: entity.batchNumber,
                movedAt: entity.movedAt,
                notes: entity.notes,
                reason: entity.reason,
                type: 'out_manual',
                quantity: entity.quantity,
                value: entity.value,
                status: 100,
            }

            if (!validateDto(dto)) return false
            await service.create(dto)
            window.api.users.statistics.tick('stock.movement')
            dispatch('change')
            return
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            if (show) return
            show = true
            if (!createdAt) {
                evaluateError('Informe data e hora de lançamento válidos')
                return false
            }
            entity.movedAt = new Date(createdAt)
            // configure timezoneoffset
            entity.movedAt.setMinutes(entity.movedAt.getMinutes() + entity.movedAt.getTimezoneOffset())
            const dto: Partial<ProductMovement> = {
                personId: entity.person?.personId,
                serviceId: entity.service?.serviceId,
                batchNumber: entity.batchNumber,
                movedAt: entity.movedAt,
                notes: entity.notes,
                reason: entity.reason,
                quantity: entity.quantity,
                value: entity.value,
                status: 100,
            }

            if (!validateDto(dto)) return false
            await service.update(entity.productMovementId, dto)
            dispatch('change')
            return
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto) {
        if (!dto.serviceId) {
            evaluateError('Informe o serviço')
            return false
        }
        if (!dto.quantity || dto.quantity < 0) {
            evaluateError('Informe a quantidade')
            return false
        }
        if (dto.value === null) {
            evaluateError('Informe o valor. Se não há valor informe 0 (zero)')
            return false
        }
        return true
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await service.remove(entity.productMovementId)
            dispatch('change')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }
</script>

<div class="root m-bottom-sm">
    <Zact>
        <div class="spacer" />
        {#if window.store.token.permissions.stock.outputs.write}
            {#if !!entity.productMovementId}
                <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={show} on:click={removeEntity} />
            {/if}
            <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={() => saveEntity()} />
        {/if}
    </Zact>
    <div class="grid-form">
        <div class="config-box mt-2 mx-2">
            <ZContainer>
                <ZContainerCol size={6} sizeM={12}>
                    <ZentitySelect fonte="servico" label="Produto/Insumo" params={{ type: 'product,consumable', price: true }} bind:value={entity.service} />
                </ZContainerCol>
                <ZContainerCol size={6} sizeM={12}>
                    <ZentitySelect fonte="pessoa" label="Pessoa (opcional)" bind:value={entity.person} />
                </ZContainerCol>
                <Zinput size={6} sizeM={12} type="number" label="Valor unitário" bind:value={entity.value} />
                <Zinput size={6} sizeM={12} type="number" label="Quantidade" bind:value={entity.quantity} />
                <Zinput size={6} sizeM={12} type="select" label="Motivo" options={manualProductMovementReasonOptions} bind:value={entity.reason} />
                <Zinput size={12} label="Anotações" bind:value={entity.notes} />
            </ZContainer>
        </div>
    </div>
</div>

<style>
    @media (max-width: 900px) {
        .root {
            display: flex;
            flex-direction: column-reverse;
        }
    }
</style>
